import {
  AUTH_STORAGE,
  AuthStorage,
  authStorageInitialState,
  DayWithHolidaysForCal,
  GetDaysByUserQueryVariables,
  getHolidaysForCal,
  getStartEndOfMonthExpanded,
  HolidaysRecord,
  year
} from "@mc-intranet/shared";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import moment from "moment";

export const modeAtom = atomWithStorage<"light" | "dark" | undefined>(
  "mcIntranetDarkMode",
  undefined
);

/**
 * Sessions saved before Strapi 5 hold a numeric `meId` and a v4 `user`
 * (`id` + `attributes`): keep the token and drop the rest, so the login page
 * reloads `me` and the profile with the new documentId.
 */
const migrateAuthStorage = () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE);
    if (!raw) return;
    const stored = JSON.parse(raw) as AuthStorage;
    const isLegacy =
      (!!stored.user && !stored.user.documentId) ||
      (!!stored.meId && /^\d+$/.test(stored.meId));
    if (isLegacy) {
      localStorage.setItem(
        AUTH_STORAGE,
        JSON.stringify({
          ...authStorageInitialState,
          isLoggedIn: !!stored.token,
          token: stored.token
        })
      );
    }
  } catch {
    localStorage.removeItem(AUTH_STORAGE);
  }
};

migrateAuthStorage();

export const authStorageAtom = atomWithStorage<AuthStorage>(
  AUTH_STORAGE,
  authStorageInitialState
);

// Atom per il mascheramento del ruolo in dev: salva il ruolo originale prima dello switch
export const originalRoleAtom = atomWithStorage<string | null>(
  "mcIntranetOriginalRole",
  null
);

export const holydaysYearsAtom = atom<HolidaysRecord<DayWithHolidaysForCal[]>>({
  [year]: getHolidaysForCal<DayWithHolidaysForCal>(year)
});

export const holydaysByYearsAtom = atom(
  (get) => {
    return get(holydaysYearsAtom);
  },
  (get, set, newValue: number | number[]) => {
    const oldValue = get(holydaysYearsAtom);
    const values = Array.isArray(newValue) ? newValue : [newValue];
    const newHolidays: HolidaysRecord<DayWithHolidaysForCal[]> = {
      ...oldValue
    };
    values.forEach((value) => {
      if (!oldValue[value]) {
        const holidays = getHolidaysForCal<DayWithHolidaysForCal>(value);
        newHolidays[value] = holidays;
      }
    });
    set(
      holydaysYearsAtom,
      newHolidays as HolidaysRecord<DayWithHolidaysForCal[]>
    );
  }
);

export const daysByUserQueryVariablesAtom = atom<GetDaysByUserQueryVariables>({
  userId: "",
  startDate: getStartEndOfMonthExpanded(moment()).startOfMonth,
  endDate: getStartEndOfMonthExpanded(moment()).endOfMonth
});

export const daysByUserQueryVariablesSelector = atom(
  (get) => {
    const state = get(daysByUserQueryVariablesAtom);
    const user = get(authStorageAtom).user;

    return {
      ...state,
      userId: state.userId ? state.userId : user?.documentId ?? ""
    } as GetDaysByUserQueryVariables;
  },
  (_get, set, newValue: GetDaysByUserQueryVariables) => {
    // const nextValue =
    //   typeof newValue === "function"
    //     ? newValue(get(daysByUserQueryVariablesAtom))
    //     : newValue;
    set(daysByUserQueryVariablesAtom, newValue as GetDaysByUserQueryVariables);
  }
);

if (process.env["NODE_ENV"] !== "production") {
  modeAtom.debugLabel = "modeAtom";
  holydaysYearsAtom.debugLabel = "holydaysYearsAtom";
  holydaysByYearsAtom.debugLabel = "holydaysByYearsAtom";
  authStorageAtom.debugLabel = "authStorageAtom";
  originalRoleAtom.debugLabel = "originalRoleAtom";
  daysByUserQueryVariablesAtom.debugLabel = "daysByUserQueryVariablesAtom";
  daysByUserQueryVariablesSelector.debugLabel =
    "daysByUserQueryVariablesSelector";
}
